import React, { useCallback, useEffect, useState } from "react";
import {
  Activity, Bot, Code, Coins, Database, ExternalLink, Globe, KeyRound, Network, Settings2, ShieldOff, Sparkles,
  TrendingUp, Unlink, UserMinus, Waves, Wrench,
} from "lucide-react";
import Card from "./Card";
import { useApi } from "../hooks/useApi";

/**
 * Recent movements in the AI estate.
 *
 * These are news, not findings — a new agent is not a risk, it is something somebody should know
 * about. Acknowledging one means "seen", not "fixed", which is why there is no severity and no
 * remediation.
 */

// The one place an alert type gets its name: AiEstateChangePanel reads it too. Covers every value
// of the backend's AiEstateChangeTypes (a test reads that file); use estateChangeTypeMeta to look up.
export const ESTATE_CHANGE_TYPE_META = {
  NewAsset:             { Icon: Bot,        tone: "text-purple-600", label: "New asset" },
  NewTool:              { Icon: Wrench,     tone: "text-blue-600",   label: "New tool" },
  ExternalToolAttached: { Icon: ExternalLink, tone: "text-orange-600", label: "External service" },
  OwnerRemoved:         { Icon: UserMinus,  tone: "text-orange-600", label: "Owner removed" },
  // The asset is still discovered, so nothing else says its graph emptied.
  RelationshipsExpired: { Icon: Unlink,     tone: "text-orange-600", label: "Relationships expired" },
  // Was missing, so containment drift rendered under the "New asset" fallback — the opposite of
  // what it means.
  ContainmentReverted:  { Icon: ShieldOff,  tone: "text-red-600",    label: "Containment undone" },
  // Behavioral feed (Phase 6). Both describe a change made outside Axaris, which is the whole
  // reason they are worth announcing.
  AgentPermissionChanged: { Icon: Activity, tone: "text-amber-600",  label: "Permissions changed" },
  AgentCredentialAdded:   { Icon: KeyRound, tone: "text-red-600",    label: "Credential added" },
  // Cost intelligence (§11a). News like the rest of this list: adopting a model and spending money
  // are both ordinary, and only the owner can tell a deliberate change from a runaway one.
  NewModelInUse:          { Icon: Sparkles, tone: "text-purple-600", label: "New model in use" },
  // Phase 11: a tier change is response policy — a downgrade is how someone would dodge an
  // approval, so it is announced where every risk manager reads.
  TierChanged:            { Icon: ShieldOff, tone: "text-purple-600", label: "Tier changed" },
  SpendSpike:             { Icon: TrendingUp, tone: "text-amber-600", label: "Spend spike" },
  BudgetThresholdCrossed: { Icon: Coins,    tone: "text-red-600",    label: "Budget threshold" },
  FirstUseOfResource:     { Icon: Database, tone: "text-blue-600",   label: "First use of a resource" },
  NewOriginCountry:       { Icon: Globe,    tone: "text-amber-600",  label: "New origin country" },
  NewOriginNetwork:       { Icon: Network,  tone: "text-amber-600",  label: "New origin network" },
  FirstObservedCallType:  { Icon: Code,     tone: "text-blue-600",   label: "New call type" },
  BehaviorDrift:          { Icon: Waves,    tone: "text-amber-600",  label: "Behavior drift" },
  ResponsePolicyChanged:  { Icon: Settings2, tone: "text-red-600",   label: "Response policy changed" },
};

/** An unmapped type shows its raw value under a neutral icon: never another type's name, never nothing. */
export const estateChangeTypeMeta = (type) =>
  ESTATE_CHANGE_TYPE_META[type] || { Icon: Activity, tone: "text-content-secondary", label: String(type) };

const formatUtcDate = (value) => new Date(value).toLocaleDateString("en-US", {
  year: "numeric", month: "short", day: "numeric", timeZone: "UTC",
});

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const secs = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (secs < 3600) return `${Math.max(1, Math.floor(secs / 60))}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

// Outside the component so the filtered list's rows and buttons survive a re-render, such as the
// Active list arriving or a Dismiss landing, rather than being rebuilt under the pointer.
function FilteredFrame({ embedded, children }) {
  return embedded ? <div>{children}</div> : <Card className="p-4 mb-4">{children}</Card>;
}

export default function AiEstateChanges({
  canManage, onOpenAsset, refreshKey, lastScannedAt, onCountChange, onUnavailable, embedded = false,
  typeFilter = null, onClearTypeFilter,
}) {
  const apiCall = useApi();
  const [alerts, setAlerts] = useState([]);
  // A failed or malformed read is not "no changes".
  const [unavailable, setUnavailable] = useState(false);
  const [busy, setBusy] = useState(null);
  // The list behind a figure in the estate change panel: null while loading, false when the read
  // failed. Kept apart from `alerts`, which is the Active list the badge counts.
  const [filtered, setFiltered] = useState(null);

  // `changesByType` counts every status within the window, and `GET changes` has no window or type
  // parameter, so read every status (an empty status) and narrow here to the same half-open window.
  useEffect(() => {
    if (!typeFilter) {
      setFiltered(null);
      return undefined;
    }
    let stale = false;
    setFiltered(null);
    const from = new Date(typeFilter.windowStart);
    const to = new Date(typeFilter.asOfUtc);
    apiCall("GET", "/ai-assets/changes?status=")
      .then((data) => {
        if (stale) return;
        if (!Array.isArray(data)) throw new Error("malformed estate changes response");
        setFiltered(data.filter((a) => {
          const at = new Date(a.detectedAt);
          return a.alertType === typeFilter.alertType && at >= from && at < to;
        }));
      })
      .catch(() => { if (!stale) setFiltered(false); });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, refreshKey]);

  const load = useCallback(async () => {
    try {
      const data = await apiCall("GET", "/ai-assets/changes?status=Active");
      if (!Array.isArray(data)) throw new Error("malformed estate changes response");
      setAlerts(data);
      setUnavailable(false);
      onUnavailable?.(false);
      onCountChange?.(data.length);
    } catch {
      setAlerts([]);
      setUnavailable(true);
      onUnavailable?.(true);
      onCountChange?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  const acknowledge = async (alert) => {
    setBusy(alert.id);
    try {
      await apiCall("PUT", `/ai-assets/changes/${alert.id}/acknowledge`);
      setAlerts((current) => {
        const next = current.filter((a) => a.id !== alert.id);
        onCountChange?.(next.length);
        return next;
      });
      setFiltered((current) => (Array.isArray(current)
        ? current.map((a) => (a.id === alert.id ? { ...a, status: "Acknowledged" } : a))
        : current));
    } finally {
      setBusy(null);
    }
  };

  // Embedded in the Action Center: the card and header live on the container.
  const Wrapper = embedded
    ? ({ children }) => <div>{children}</div>
    : ({ children }) => <Card className="p-4 mb-4">{children}</Card>;

  const renderRow = (alert) => {
    const meta = estateChangeTypeMeta(alert.alertType);
    const { Icon } = meta;
    return (
      <div key={alert.id} className="flex items-start gap-2 text-sm">
        <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${meta.tone}`} />
        <div className="min-w-0 flex-1">
          <button
            onClick={() => alert.aiAssetId && onOpenAsset?.(alert.aiAssetId)}
            disabled={!alert.aiAssetId}
            className="text-left text-content-primary hover:underline disabled:hover:no-underline disabled:cursor-default"
          >
            {alert.title}
          </button>
          {alert.detail && (
            <div className="text-[11px] text-content-secondary">{alert.detail}</div>
          )}
        </div>
        {alert.status && alert.status !== "Active" && (
          <span className="text-[11px] text-content-secondary shrink-0">{alert.status}</span>
        )}
        <span className="text-[11px] text-content-secondary shrink-0">
          {timeAgo(alert.detectedAt)}
        </span>
        {canManage && (!alert.status || alert.status === "Active") && (
          <button
            onClick={() => acknowledge(alert)}
            disabled={busy === alert.id}
            className="text-[11px] text-content-secondary hover:text-content-primary disabled:opacity-50 shrink-0"
            title="Mark as seen"
          >
            Dismiss
          </button>
        )}
      </div>
    );
  };

  // Opened from a figure in the estate change panel: every row is shown, so it can be counted
  // against that figure, and the heading says how this differs from the usual recent list.
  if (typeFilter) {
    const label = estateChangeTypeMeta(typeFilter.alertType).label;
    return (
      <FilteredFrame embedded={embedded}>
        <div className="flex flex-wrap items-baseline gap-2 mb-2" data-testid="changes-type-filter">
          <span className="text-sm font-semibold text-content-primary">
            {label} alerts — {typeFilter.windowDays} days
          </span>
          {Array.isArray(filtered) && (
            <span className="text-[11px] text-content-secondary">
              {filtered.length} in any status, {formatUtcDate(typeFilter.windowStart)} to {formatUtcDate(typeFilter.asOfUtc)}
            </span>
          )}
          <button
            onClick={() => onClearTypeFilter?.()}
            className="ml-auto text-[11px] text-content-secondary hover:text-content-primary hover:underline"
          >
            Show all recent changes
          </button>
        </div>
        {filtered === null && <p className="text-sm text-content-secondary">Loading…</p>}
        {filtered === false && (
          <p className="text-sm text-content-secondary" data-testid="changes-filter-unavailable">
            These alerts are unavailable right now.
          </p>
        )}
        {Array.isArray(filtered) && filtered.length === 0 && (
          <p className="text-sm text-content-secondary">No alerts of this type in this window.</p>
        )}
        {Array.isArray(filtered) && <div className="space-y-1.5">{filtered.map(renderRow)}</div>}
      </FilteredFrame>
    );
  }

  if (unavailable) {
    return (
      <Wrapper>
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-content-secondary" />
          <span className="text-sm text-content-secondary" data-testid="changes-unavailable">
            Estate changes are unavailable right now.
          </span>
        </div>
      </Wrapper>
    );
  }

  if (alerts.length === 0) {
    // Vanishing on empty made "nothing changed" indistinguishable from "change detection never
    // ran" — the same ambiguity the behavioral feed's coverage row exists to prevent. So: stay
    // hidden only before the first scan, when there is genuinely nothing to report on.
    if (!lastScannedAt) return null;
    return (
      <Wrapper>
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-content-secondary" />
          <span className="text-sm text-content-secondary">
            No estate changes since the last scan
            <span className="text-[11px]"> · checked {timeAgo(lastScannedAt)}</span>
          </span>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {!embedded && (
        <div className="flex items-center gap-2 mb-2">
          <Activity className="w-4 h-4 text-content-secondary" />
          <span className="text-sm font-semibold text-content-primary">
            What changed recently
          </span>
          <span className="text-[11px] text-content-secondary">
            {alerts.length} unacknowledged
          </span>
        </div>
      )}

      <div className="space-y-1.5">
        {alerts.slice(0, 8).map(renderRow)}
      </div>

      {alerts.length > 8 && (
        <div className="mt-2 text-[11px] text-content-secondary">
          +{alerts.length - 8} more
        </div>
      )}
    </Wrapper>
  );
}
